import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, Sparkles } from 'lucide-react';
import { ConfidenceBadge } from '@/components/ai-accountant/confidence-chip';
import { AiStatusChip } from '@/components/ai-accountant/status-chip';
import { EmptyState, PageHeader } from '@/components/page-header';
import { FileDrop } from '@/components/portal/file-drop';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  RECORD_AS_LABEL,
  useAiDocuments,
  useAiSettings,
  useAiSummary,
  type AiDocument,
  type AiFilter,
} from '@/lib/ai-accountant';
import { api } from '@/lib/api';
import { formatAmount, formatDate, formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { documentPath, errorText, isClientRole, uploadFile, useOrgRole } from '@/lib/portal';
import { cn } from '@/lib/utils';

const CHIPS: Array<[AiFilter, string]> = [
  ['processing', 'Processing'],
  ['for_review', 'For review'],
  ['recorded', 'Recorded'],
  ['rejected', 'Rejected'],
  ['failed', 'Failed'],
  ['all', 'All'],
];

/** PDFs and JPG or PNG photos (the server checks again by content). */
const AI_ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const aiFileOk = (f: File) =>
  /^(application\/pdf|image\/png|image\/jpeg)$/.test(f.type) || /\.(pdf|png|jpe?g)$/i.test(f.name);

export const Route = createFileRoute('/ai-accountant/')({
  component: AiAccountant,
  validateSearch: (s: Record<string, unknown>): { status?: AiFilter } =>
    typeof s['status'] === 'string' && CHIPS.some(([k]) => k === s['status'])
      ? { status: s['status'] as AiFilter }
      : {},
});

/**
 * Screen AI1 (document 06): AI Accountant. Upload bills and receipts; each is read (OCR),
 * categorised against the chart of accounts and waits For review. Nothing is recorded until a
 * person approves it.
 */
function AiAccountant() {
  const orgId = useOrgId();
  const role = useOrgRole();
  const { status = 'for_review' } = Route.useSearch();
  const navigate = useNavigate();
  const list = useAiDocuments(status);
  const summary = useAiSummary();
  const settings = useAiSettings();
  const qc = useQueryClient();

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const f of files) await uploadFile(`/orgs/${orgId}/ai-accountant`, f);
    },
    onSuccess: (_r, files) => {
      toast(
        files.length === 1
          ? `${files[0]!.name} uploaded. The AI Accountant is reading it.`
          : `${files.length} files uploaded. The AI Accountant is reading them.`,
      );
      if (status !== 'processing' && status !== 'all')
        void navigate({ to: '/ai-accountant', search: { status: 'processing' } });
    },
    onError: (e) => toast(errorText(e), { tone: 'error' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['ai-accountant', orgId] }),
  });
  const rerun = useMutation({
    mutationFn: (d: AiDocument) =>
      api(`/orgs/${orgId}/ai-accountant/${d.id}/rerun`, {
        method: 'POST',
        body: JSON.stringify({ version: d.version }),
      }),
    onSuccess: () => toast('Sent to the AI Accountant again.'),
    onError: (e) => toast(errorText(e), { tone: 'error' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['ai-accountant', orgId] }),
  });

  if (!orgId)
    return <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />;
  if (role && isClientRole(role))
    return <EmptyState title="Only accounting staff use the AI Accountant" />;
  const rows = list.data ?? [];
  const counts = summary.data;
  const s = settings.data;
  return (
    <>
      <PageHeader
        title="AI Accountant"
        description="Upload bills and receipts. Each one is read, matched to a vendor and categorised against the chart of accounts, then waits for you to review and approve it."
      />
      <div className="mb-5 grid gap-3 rounded-lg border bg-card p-4 md:grid-cols-[1fr_280px]">
        <FileDrop
          disabled={upload.isPending}
          accept={AI_ACCEPT}
          types="PDF, JPG or PNG"
          label={upload.isPending ? 'Uploading…' : 'Drag and drop bills and receipts here, or'}
          onFiles={(files) => {
            const ok = files.filter(aiFileOk);
            for (const f of files.filter((x) => !aiFileOk(x)))
              toast(`${f.name} is not a PDF, JPG or PNG.`, { tone: 'error' });
            if (ok.length) upload.mutate(ok);
          }}
          onProblem={(m) => toast(m, { tone: 'error' })}
        />
        <div className="space-y-2 text-sm" data-testid="ai-engine">
          <p className="flex items-center gap-1.5 font-medium">
            <Sparkles className="size-4 text-primary" aria-hidden />
            {s?.typesafeConfigured && s.sendToTypesafe
              ? `Categorised by TypeSafe Jev (${s.model})`
              : 'Categorised by rules (vendor history and keywords)'}
          </p>
          <p className="text-muted-foreground">
            {s?.typesafeConfigured && s.sendToTypesafe
              ? 'Only the text read from each file and the names in your chart of accounts and vendor list are sent; never the file.'
              : s?.typesafeConfigured
                ? 'TypeSafe is available. Switch it on for this client in Company settings › Advanced.'
                : 'Nothing leaves this server.'}
          </p>
          <p className="text-muted-foreground">
            Up to {s?.maxPages ?? 5} pages are read per file. A person approves every entry.
          </p>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="Status">
        {CHIPS.map(([k, label]) => {
          const n = k === 'all' ? null : counts?.[k];
          return (
            <button
              key={k}
              role="tab"
              aria-selected={status === k}
              className={cn(
                'rounded-full border px-3 py-1 text-sm',
                status === k ? 'border-primary bg-primary/10 text-primary' : 'bg-card',
              )}
              onClick={() => void navigate({ to: '/ai-accountant', search: { status: k } })}
            >
              {label}
              {n ? <span className="tnum ml-1.5 text-xs text-muted-foreground">{n}</span> : null}
            </button>
          );
        })}
      </div>

      {list.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.isError ? (
        <EmptyState title="Could not load the documents" hint={list.error.message} />
      ) : !rows.length ? (
        <EmptyState
          title={status === 'for_review' ? 'Nothing to review' : 'No documents here'}
          hint="Uploaded bills and receipts appear here once they are read."
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Uploaded</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead className="w-[100px]">Date</TableHead>
                <TableHead className="w-[140px] text-right">Total</TableHead>
                <TableHead className="w-[110px]">Record as</TableHead>
                <TableHead className="w-[120px]">Confidence</TableHead>
                <TableHead className="w-[110px]">Status</TableHead>
                <TableHead className="w-[150px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((d) => (
                <TableRow
                  key={d.id}
                  data-testid="ai-row"
                  className="cursor-pointer"
                  onClick={() => void navigate({ to: '/ai-accountant/$id', params: { id: d.id } })}
                >
                  <TableCell className="tnum">{formatDateTime(d.uploadedAt)}</TableCell>
                  <TableCell className="max-w-[240px] truncate font-medium">{d.fileName}</TableCell>
                  <TableCell className="max-w-[220px] truncate">{d.vendorName}</TableCell>
                  <TableCell className="tnum">
                    {d.documentDate ? formatDate(d.documentDate) : ''}
                  </TableCell>
                  <TableCell className="tnum text-right">
                    {d.total ? `${d.currency ?? ''} ${formatAmount(d.total)}` : ''}
                  </TableCell>
                  <TableCell>{d.recordAs ? RECORD_AS_LABEL[d.recordAs] : ''}</TableCell>
                  <TableCell>
                    {d.status === 'for_review' ? <ConfidenceBadge value={d.confidence} /> : null}
                  </TableCell>
                  <TableCell>
                    <AiStatusChip status={d.status} />
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <RowAction d={d} onRerun={() => rerun.mutate(d)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

function RowAction({ d, onRerun }: { d: AiDocument; onRerun: () => void }) {
  if (d.status === 'processing')
    return (
      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-hidden /> Reading…
      </span>
    );
  if (d.status === 'for_review')
    return (
      <Button size="sm" asChild>
        <Link to="/ai-accountant/$id" params={{ id: d.id }}>
          Review
        </Link>
      </Button>
    );
  if (d.status === 'failed')
    return (
      <Button size="sm" variant="outline" onClick={onRerun}>
        Re-run AI
      </Button>
    );
  if (d.status === 'recorded' && d.document)
    return (
      <Link
        className="text-sm text-primary hover:underline"
        to={documentPath(d.document.kind, d.document.id)}
      >
        {d.document.kind === 'expense' ? 'Expense' : 'Bill'} {d.document.number ?? ''}
      </Link>
    );
  if (d.status === 'recorded' && d.bankImport)
    return (
      <Link
        className="text-sm text-primary hover:underline"
        to="/banking"
        search={{ accountId: d.bankImport.accountId }}
      >
        Banking ({d.bankImport.lines})
      </Link>
    );
  if (d.status === 'recorded' && d.journalEntry)
    return (
      <Link
        className="text-sm text-primary hover:underline"
        to="/accounting/journal-entries/$entryId"
        params={{ entryId: d.journalEntry.id }}
      >
        Journal {d.journalEntry.entryNo}
      </Link>
    );
  return (
    <Link
      className="text-sm text-primary hover:underline"
      to="/ai-accountant/$id"
      params={{ id: d.id }}
    >
      View
    </Link>
  );
}
